import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/auth/useAuth';

/**
 * The bell's data (fleet step 3, 2026-08-30).
 *
 * One row per thing that needs you: a request for Vic, a send / decline /
 * date change / arrival for the requester, a machine on its way for the
 * superintendent. The server keeps the list; the client keeps two views of
 * it in cache — the unread rows with their count (the pill), and the latest
 * thirty including read (the panel) — and marks read optimistically so the
 * pill drops the moment you click, not when the server gets round to it.
 */

export type NotificationKind =
  | 'move.requested' | 'move.approved' | 'move.dates_changed' | 'move.declined'
  | 'move.departed' | 'move.arrived' | 'move.claim_overridden' | 'rental.added';

/** One row of the bell. Named so it never shadows the DOM's global `Notification`. */
export interface UserNotification {
  id: string;
  kind: NotificationKind | string;
  title: string;
  body: string | null;
  /** Bare SPA path — `/equipment/{ID}` or `/equipment/inbox?request={ID}`. */
  link: string;
  entity_type: string | null;
  entity_id: string | null;
  read_at: string | null;
  created_at: string;
}

/** The envelope both GETs return: rows plus the total unread. */
export interface NotificationsPage {
  data: UserNotification[];
  meta: { unread: number };
}

export const notificationKeys = {
  all: ['notifications'] as const,
  /** `?unread=1` — the pill's poll: unread rows and the count. */
  unread: () => ['notifications', 'unread'] as const,
  /** The panel's list: latest 30, read included. */
  list: () => ['notifications', 'list'] as const,
};

const POLL_MS = 60_000;

/**
 * Polls the unread count once a minute while logged in. `retry: false` and
 * no error surface on purpose: the PWA runs offline, and a poll that cannot
 * reach the server must fail quietly — the pill simply keeps its last value.
 */
export function useUnreadCount() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: () => api.get<NotificationsPage>('/api/v1/me/notifications?unread=1'),
    enabled: !!isAuthenticated,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 5_000,
    retry: false,
  });
}

/** The panel's list — fetched only while the panel is open, fresh each time.
 *  `staleTime: 0` on purpose: the app's five-minute default would hand a
 *  reopened panel the old list while the pill had already moved on. */
export function useNotifications(enabled: boolean) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => api.get<NotificationsPage>('/api/v1/me/notifications'),
    enabled: enabled && !!isAuthenticated,
    staleTime: 0,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Optimistic cache edits shared by the two mutations.

interface Snapshot {
  unread: NotificationsPage | undefined;
  list: NotificationsPage | undefined;
}

function snapshot(qc: QueryClient): Snapshot {
  return {
    unread: qc.getQueryData<NotificationsPage>(notificationKeys.unread()),
    list: qc.getQueryData<NotificationsPage>(notificationKeys.list()),
  };
}

function restore(qc: QueryClient, snap: Snapshot | undefined) {
  if (!snap) return;
  qc.setQueryData(notificationKeys.unread(), snap.unread);
  qc.setQueryData(notificationKeys.list(), snap.list);
}

/** Stamp `read_at` on the rows `match` picks, in both cached views, and drop the count by how many were actually unread. */
function markInCache(qc: QueryClient, match: (row: UserNotification) => boolean) {
  const now = new Date().toISOString();
  const stamp = (page: NotificationsPage | undefined, keepRead: boolean): NotificationsPage | undefined => {
    if (!page) return page;
    let marked = 0;
    const rows: UserNotification[] = [];
    for (const row of page.data) {
      if (!match(row) || row.read_at) { rows.push(row); continue; }
      marked += 1;
      if (keepRead) rows.push({ ...row, read_at: now });
    }
    return { data: rows, meta: { unread: Math.max(0, (page.meta?.unread ?? 0) - marked) } };
  };
  // The unread view holds only unread rows, so a marked row leaves it.
  qc.setQueryData(notificationKeys.unread(), (page: NotificationsPage | undefined) => stamp(page, false));
  qc.setQueryData(notificationKeys.list(), (page: NotificationsPage | undefined) => stamp(page, true));
}

/** POST /me/notifications/{id}/read — optimistic; the server's answer wins on settle. */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: UserNotification; meta: { unread: number } }>(`/api/v1/me/notifications/${id}/read`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const snap = snapshot(qc);
      markInCache(qc, (row) => row.id === id);
      return snap;
    },
    onError: (_err, _id, snap) => restore(qc, snap),
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/** POST /me/notifications/read-all — optimistic; everything unread is stamped at once. */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ data: { marked: number }; meta: { unread: number } }>('/api/v1/me/notifications/read-all'),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const snap = snapshot(qc);
      markInCache(qc, () => true);
      return snap;
    },
    onError: (_err, _vars, snap) => restore(qc, snap),
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
